'use client';
import React, { useState, ReactNode, useCallback, useEffect } from 'react';
import {
  ModalContext,
  SwapSearchModalProps,
  CreateVaultExistsModalProps,
  CreateVaultOpenseaModalProps,
  CreateVaultInvalidInterfaceProps,
  CreateInventoryPositionProps,
  CreateLiquidityPositionProps,
  SwapModalProps,
  ManageLiquidityPositionModalProps,
  ManageInventoryPositionModalProps,
  CreatePositionModalProps,
  ClaimRewardsProps,
  WithdrawLiquidityProps,
  WithdrawInventoryProps,
  IncreaseLiquidityProps,
  AssetDetailModalProps,
} from './ModalContext';
import SwapModal from '@/presentation/components/modals/SwapModal';
import SwapSearchModal from '@/presentation/components/modals/SwapSearchModal';
import CreateVaultExistsModal from '@/presentation/components/modals/CreateVaultExistsModal';
import CreateVaultOpenseaModal from '@/presentation/components/modals/CreateVaultOpenseaModal';
import CreateVaultInvalidInterfaceModal from '@/presentation/components/modals/CreateVaultInvalidInterfaceModal';
import CreateInventoryPositionModal from '@/presentation/components/modals/CreateInventoryPositionModal';
import CreateLiquidityPositionModal from '@/presentation/components/modals/CreateLiquidityPositionModal';
import ManageLiquidityPositionModal from '@/presentation/components/modals/ManageLiquidityPositionModal';
import ManageInventoryPositionModal from '@/presentation/components/modals/ManageInventoryPositionModal';
import CreatePositionModal from '@/presentation/components/modals/CreatePositionModal';
import ClaimRewardsModal from '@/presentation/components/modals/ClaimRewardsModal';
import WithdrawLiquidityModal from '@/presentation/components/modals/WithdrawLiquidityModal';
import WithdrawInventoryModal from '@/presentation/components/modals/WithdrawInventoryModal';
import IncreaseLiquidityModal from '@/presentation/components/modals/IncreaseLiquidityModal';
import AssetDetailModal from '@/presentation/components/modals/AssetDetailModal';
import FaucetModal from '@/presentation/components/modals/FaucetModal';
import { BugBountyModal } from '@/presentation/components/modals/BugBountyModal';
import {
  getItem,
  LOCAL_STORAGE_KEYS,
  setItem,
} from '@/presentation/utils/storage';
import { DismissableModal } from '@/presentation/components/modals/DismissableModal';
import MigrateModal, {
  MigrateModalProps,
} from '@/presentation/components/modals/MigrateModal';
import MigrateSlpModal, {
  MigrateSlpModalProps,
} from '@/presentation/components/modals/MigrateSlpModal';
import MigrateXSlpModal, {
  MigrateXSlpModalProps,
} from '@/presentation/components/modals/MigrateXSlpModal';
import MigrateXTokenModal, {
  MigrateXTokenModalProps,
} from '@/presentation/components/modals/MigrateXTokenModal';
import MigrateVTokenModal, {
  MigrateVTokenModalProps,
} from '@/presentation/components/modals/MigrateVTokenModal';

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modal, setModal] = useState<JSX.Element | null>(null);

  const closeModal = useCallback((key?: string) => {
    setModal(null);
    // If we pass a storage key, add to session - used for popups on landing
    if (key) {
      setItem(key, 'dismissed');
    }
  }, []);

  const [autoShowModal] = useState(() => ({
    modal: <BugBountyModal />,
    key: LOCAL_STORAGE_KEYS.bugBounty,
  }));

  const showSwapModal = (props: SwapModalProps) => {
    return setModal(() => <SwapModal {...props} />);
  };

  const showSwapSearchModal = (props: SwapSearchModalProps) => {
    return setModal(() => <SwapSearchModal {...props} />);
  };

  const showCreateVaultExistsModal = (props: CreateVaultExistsModalProps) => {
    return setModal(() => <CreateVaultExistsModal {...props} />);
  };

  const showCreateVaultOpenseaModal = (props: CreateVaultOpenseaModalProps) =>
    setModal(() => <CreateVaultOpenseaModal {...props} />);

  const showCreateVaultInvalidInterfaceModal = (
    props: CreateVaultInvalidInterfaceProps
  ) => setModal(() => <CreateVaultInvalidInterfaceModal {...props} />);

  const showCreateInventoryPositionModal = (
    props: CreateInventoryPositionProps
  ) => setModal(() => <CreateInventoryPositionModal {...props} />);

  const showCreateLiquidityPositionModal = (
    props: CreateLiquidityPositionProps
  ) => setModal(() => <CreateLiquidityPositionModal {...props} />);

  const showManageLiquidityPositionModal = (
    props: ManageLiquidityPositionModalProps
  ) => setModal(() => <ManageLiquidityPositionModal {...props} />);

  const showManageInventoryPositionModal = (
    props: ManageInventoryPositionModalProps
  ) => setModal(() => <ManageInventoryPositionModal {...props} />);

  const showCreatePositionModal = (props: CreatePositionModalProps) =>
    setModal(() => <CreatePositionModal {...props} />);

  const showClaimRewardsModal = (props: ClaimRewardsProps) =>
    setModal(() => <ClaimRewardsModal {...props} />);
  const showWithdrawLiquidityModal = (props: WithdrawLiquidityProps) =>
    setModal(() => <WithdrawLiquidityModal {...props} />);
  const showWithdrawInventoryModal = (props: WithdrawInventoryProps) =>
    setModal(() => <WithdrawInventoryModal {...props} />);
  const showIncreaseLiquidityModal = (props: IncreaseLiquidityProps) =>
    setModal(() => <IncreaseLiquidityModal {...props} />);
  const showFaucetModal = () => setModal(() => <FaucetModal />);

  const showAssetDetailModal = (props: AssetDetailModalProps) =>
    setModal(() => <AssetDetailModal {...props} />);

  useEffect(() => {
    if (!modal) {
      // If this modal hasn't been dismissed, show.
      const key = getItem(autoShowModal.key);
      if (!key) {
        setModal(() => (
          <DismissableModal modalKey={autoShowModal.key}>
            {autoShowModal.modal}
          </DismissableModal>
        ));
      }
    }
  }, [autoShowModal, modal, setModal]);
  const showMigrateModal = (props: MigrateModalProps) =>
    setModal(() => <MigrateModal {...props} />);
  const showMigrateSlpModal = (props: MigrateSlpModalProps) =>
    setModal(() => <MigrateSlpModal {...props} />);
  const showMigrateXSlpModal = (props: MigrateXSlpModalProps) =>
    setModal(() => <MigrateXSlpModal {...props} />);
  const showMigrateXTokenModal = (props: MigrateXTokenModalProps) =>
    setModal(() => <MigrateXTokenModal {...props} />);
  const showMigrateVTokenModal = (props: MigrateVTokenModalProps) =>
    setModal(() => <MigrateVTokenModal {...props} />);

  return (
    <ModalContext.Provider
      value={{
        modal,
        setModal,
        closeModal,
        showSwapModal,
        showSwapSearchModal,
        showCreateVaultExistsModal,
        showCreateVaultOpenseaModal,
        showCreateVaultInvalidInterfaceModal,
        showCreateInventoryPositionModal,
        showCreateLiquidityPositionModal,
        showManageLiquidityPositionModal,
        showManageInventoryPositionModal,
        showCreatePositionModal,
        showClaimRewardsModal,
        showWithdrawLiquidityModal,
        showWithdrawInventoryModal,
        showIncreaseLiquidityModal,
        showFaucetModal,
        showAssetDetailModal,
        showMigrateModal,
        showMigrateSlpModal,
        showMigrateVTokenModal,
        showMigrateXSlpModal,
        showMigrateXTokenModal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};
